import type * as React from 'react';

import Icon from '../../primitives/icon/index';
import { useDesktopHeaderTracking } from '../header/context/DesktopHeaderTrackingContext';

import styles from './LoggedInMiniMyOC.module.css';

// A single menu entry, fully resolved by the consuming app's glue: label + href are already built,
// `external` adds rel="noreferrer", `key` drives the React key and the `myoc-${key}` test id.
export interface MiniMyOCItem {
  external?: boolean;
  href: string;
  key: string;
  name: string;
}

interface LoggedInMiniMyOCProps {
  // Already-built menu entries (profile/settings/subscriptions/…), in display order.
  items: readonly MiniMyOCItem[];
  logoutLabel: string;
  onLogout?: () => void;
  // Href for the collapsed (burgerTop/pageButton) account-icon link.
  profileHref: string;
  // Resolved dropdown heading (e.g. "My Oddschecker").
  title: string;
  type: 'desktop' | 'burgerTop' | 'pageButton';
}

const UserIcon: React.FC = () => <Icon data-testid="user-icon" icon="User" />;

// Presentational, context-agnostic logged-in account menu. Each app builds `items`/`title`/hrefs its
// own way (Remix LabelService vs react-i18next, relative vs repub-prefixed hrefs) and feeds them in.
const LoggedInMiniMyOC: React.FC<LoggedInMiniMyOCProps> = ({
  items,
  logoutLabel,
  onLogout,
  profileHref,
  title,
  type,
}) => {
  const track = useDesktopHeaderTracking();

  const handleLogout = (): void => {
    onLogout?.();
  };

  if (type === 'desktop') {
    const handleLogoutClick = (): void => {
      track('account', logoutLabel);
      handleLogout();
    };

    return (
      <div className={styles.myOC} data-testid="mini-myoc">
        <span className={styles.myOCIconWrapper}>
          <UserIcon />
          <span className={styles.indicator}> &#x25BE;</span>
        </span>
        <div className={styles.myOCDropdown} data-testid="myoc-dropdown">
          <span className={styles.upArrow} />
          <div className={styles.myOCTitle} data-testid="myoc-title">
            {title}
          </div>
          {items.map(item => (
            <a
              className={styles.myOCLink}
              href={item.href}
              key={item.key}
              onClick={() => track('account', item.name)}
              rel={item.external ? 'noreferrer' : undefined}
              title={item.name}
            >
              <div className={styles.myOCRow} data-testid={`myoc-${item.key}`}>
                {item.name}
              </div>
            </a>
          ))}
          <div
            className={[styles.myOCRow, styles.lastRow].join(' ')}
            data-testid="logout-button"
            onClick={handleLogoutClick}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleLogoutClick();
              }
            }}
            role="button"
            tabIndex={0}
          >
            {logoutLabel}
          </div>
        </div>
      </div>
    );
  }

  // Collapsed affordance (burgerTop/pageButton): a single account icon linking to the profile page.
  return (
    <a className={styles.myOCLink} href={profileHref} title={title}>
      <span className={styles.myOCIconWrapper}>
        <UserIcon />
      </span>
    </a>
  );
};

export default LoggedInMiniMyOC;
